import { observer } from "mobx-react-lite";
import { useStore } from "../../store";
import { Outcome } from "../../types/game";
import { useABButtons } from "../../hooks/useABButtons";
import { Header } from "../../components/Header/Header";
import { TextButton } from "../../components/TextButton/TextButton";

import InactivityManager from "../../components/InactivityManager/InactivityManager";
import Card from "../../components/Card/Card";
import { useTranslation } from "react-i18next";
import { LargeSizeCard, SmallSizeCard } from "../../components/SizeCard/SizeCard";
import { ViewShell } from "../../components/Layout/ViewShell";

export const OutcomeView = observer(() => {
  const { mainStore } = useStore();
  const currentNode = mainStore.getCurrentNode();
  const { promptState, resetInactivity } = useABButtons();
  const { t } = useTranslation();

  function onButtonAClick() {
    mainStore.makeChoice("a");
    resetInactivity();
  }

  function onButtonBClick() {
    mainStore.makeChoice("b");
    resetInactivity();
  }

  const node = currentNode;
  if (!node) {
    return null;
  }

  const { id, title, description, image } = node as Outcome;

  const isSizeOutcome = ["small-shark-outcome", "large-shark-outcome"].includes(id);
  const isSmallSizeOutcome = title.includes("Small");

  const stepCount = mainStore.getCurrentStepCount;
  let legend = "";
  let showBButton = true;

  if (stepCount !== 4) {
    legend = t("step", { stepCount: stepCount.toString() });
  } else {
    legend = t("yourShark");
    showBButton = false;
  }

  const renderDescription = () => {
    if (typeof description === "string") {
      return <p className="outcome-description">{t(description)}</p>;
    }

    return (
      <div className="outcome-description-container">
        <p className="outcome-description final-outcome">
          <span className="outcome-description-bold">{t("range")}</span>
          {t(`${id}.description.range`)}
        </p>
        <p className="outcome-description final-outcome">
          <span className="outcome-description-bold">{t("size")}</span>
          {t(`${id}.description.size`)}
        </p>
        <p className="outcome-description final-outcome">
          <span className="outcome-description-bold">{t("funFact")}</span>
          {t(`${id}.description.fact`)}
        </p>
      </div>
    );
  };

  const renderMedia = () => {
    if (image && !isSizeOutcome) {
      return <Card title="" imageUrl={image} size="lg" />;
    }

    if (isSizeOutcome && isSmallSizeOutcome) {
      return <SmallSizeCard />;
    }

    return <LargeSizeCard />;
  };

  return (
    <ViewShell
      variant="outcome"
      id={id}
      aria-labelledby={`${id}-title`}
      data-variant={isSizeOutcome ? "size" : "standard"}
    >
      {showBButton && (
        <div className="outcome-view__top-action view-shell__overlay">
          <TextButton label="B" id="b-button" onClick={onButtonBClick} />
          <p className="outcome-button-text--b">{t("restart")}</p>
        </div>
      )}

      <div className="view-shell__content outcome-view__content">
        <div className="view-shell__title" id={`${id}-title`}>
          <Header legend={legend} title={!showBButton ? t(`${id}.name`) : t(title)} />
        </div>

        <div className="outcome-view__grid">
          <div className="outcome-view__media">{renderMedia()}</div>
          <div className="outcome-view__details">{renderDescription()}</div>
        </div>
      </div>

      <div className="view-shell__footer outcome-view__footer view-shell__overlay">
        <TextButton label="A" id="a-button" onClick={onButtonAClick} />
        <p className="outcome-button-text--a">{showBButton ? t("continue") : t("restart")}</p>
      </div>

      <InactivityManager promptState={promptState} resetInactivity={resetInactivity} />
    </ViewShell>
  );
});
